import { isNative } from "@/lib/capacitor";
import { supabase } from "@/integrations/supabase/client";
import { getOAuthRedirectUrl } from "@/lib/authRedirect";

// signInWithApple — branches by platform.
//
// On iOS (Capacitor): calls the native @capacitor-community/apple-sign-in
// plugin to open Apple's system sheet, gets an identity token signed by
// Apple, and hands it to Supabase via signInWithIdToken — no WebView
// redirect, no deep link round-trip. This is what Apple guideline 4.8
// requires; the prior web-OAuth path silently failed inside the WebView
// and got the build rejected.
//
// On web: keeps the original signInWithOAuth redirect flow. Web doesn't
// have access to the native plugin, and OAuth in a regular browser works
// fine.
//
// Returns { ok: true } when the sign-in completed (or the redirect
// kicked off cleanly on web), { ok: false, cancelled: true } when the
// user dismissed the Apple sheet, and throws on any unexpected error.

interface AppleSignInResult {
  ok: boolean;
  cancelled?: boolean;
}

export async function signInWithApple(): Promise<AppleSignInResult> {
  if (isNative) {
    return signInWithAppleNative();
  }
  return signInWithAppleWeb();
}

async function signInWithAppleNative(): Promise<AppleSignInResult> {
  const { AppleSignIn, SignInScope } = await import(
    "@capawesome/capacitor-apple-sign-in"
  );

  let result;
  try {
    // No initialize() needed on iOS — the native flow uses the app's
    // entitlement-declared identifier automatically. Initialize is only
    // required on Android/Web where the Service ID matters.
    result = await AppleSignIn.signIn({
      scopes: [SignInScope.Email, SignInScope.FullName],
    });
  } catch (e) {
    const err = e as { code?: string; message?: string };
    // Apple's cancel codes vary across platforms; match a few defensively.
    const msg = (err.message ?? "").toLowerCase();
    if (
      err.code === "ERR_CANCELED" ||
      err.code === "CANCELED" ||
      msg.includes("cancel") ||
      msg.includes("dismissed")
    ) {
      return { ok: false, cancelled: true };
    }
    throw e;
  }

  const idToken = result?.idToken;
  if (!idToken) {
    throw new Error("Apple sign-in returned no identity token.");
  }

  const { error } = await supabase.auth.signInWithIdToken({
    provider: "apple",
    token: idToken,
  });
  if (error) throw error;
  return { ok: true };
}

async function signInWithAppleWeb(): Promise<AppleSignInResult> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: { redirectTo: getOAuthRedirectUrl("/auth/callback") },
  });
  if (error) throw error;
  return { ok: true };
}
